import { useEffect, useState } from 'react';
import { Outlet, useParams } from 'react-router-dom';

import { Box } from '@mui/material';

import SettlementsTableDataRow from './SettlementsTableDataRow';
import { useSettlementConfig } from '../../../hooks/useSettlementConfig';

import { getSettlements } from '../../../api/paymentsApi/settlementsApi';
import Loader from '../../../components/Loader';
import NoDataText from '../../../components/NoDataText';
import TableContainer from '../../../components/Table/TableContainer';
import {
  getCasesSettlementsListTableLabels,
  getSettlementsListTableLabels,
} from '../../../constants/tableLabels';
import usePagination from '../../../hooks/usePagination';
import { ActiveSortField, FilterTypes, SettlementShort } from '../../../types';
import TableFilters from '../../../components/TableFilters/TableFilters';
import { downloadCsvorExcel } from '../../../helpers/downloadCsv';
import TableActions from '../../../components/Table/TableActions';
import { APPROVAL_STATUS_FILTER_OPTIONS, ONBOARDING_STATUS_FILTER_OPTIONS, SETTLEMENT_TYPE_FILTER_OPTIONS, SETTLEMENTS_STATUS_FILTER_OPTIONS } from '../../../components/TableFilters/constants';
import useSnackBar from '../../../hooks/useSnackBar';
import { handleSortTableData } from '../../../helpers/handleSortTableData';
import { useHasPermission } from '../../../hooks/useHasPermission';

const SettlementsView = () => {
  const params = useParams();

  const { pageNumber, rowsPerPage, setTotalCount } = usePagination();
  const { setAlert } = useSnackBar();
  const { setEnableMemoUpload } = useSettlementConfig();
  const canCreateSettlement = useHasPermission('Payments', 'Create Settlement');

  const [settlementsList, setSettlementsList] = useState<SettlementShort[]>([]);
  const [isDataLoading, setIsDataLoading] = useState(true);
  const [showFilters, setShowFilters] = useState<boolean>(true);


  const [filters, setFilters] = useState<FilterTypes>({
    clientId: '',
    defendantId: '',
  });
  const [match, setMatch] = useState<string>('');
  const [activeSortField, setActiveSortField] = useState<ActiveSortField | null>(null);
  const sortableFields = {
    createdAt: {
      order: '',
      fieldName: 'createdAt',
    },
    paidAt: {
      order: '',
      fieldName: 'paidAt',
    },
    updatedAt: {
      order: '',
      fieldName: 'updatedAt',
    },
  };

  const hasParams = !!Object.entries(params).length;

  useEffect(() => {
    getSettlements({
      page: pageNumber,
      size: rowsPerPage,
      count: pageNumber === 1,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
      activeSortField,
    }).then((res) => {
      if (res.data) {
        pageNumber === 1 && setTotalCount?.(res.data.totalCount || 0);
        setSettlementsList(res.data.resultSet);
        // Use custom hook to set global state
        setEnableMemoUpload(res.data.enableMemoUpload);
        setIsDataLoading(false);
      }
    });
  }, [pageNumber, rowsPerPage, filters, match, activeSortField]);

  const exportList = () => {
    downloadCsvorExcel('/v1/fo/settlements/export', 'POST', 'Settlements_List','excel', {
      page: pageNumber,
      size: rowsPerPage,
      match: match,
      filters: { ...filters, ...(hasParams && params) },
    }).then((res) => {
      if (res?.data) {
        setAlert({
          message: 'Settlements exported successfully.',
          type: 'success',
        });
      }
    });
  };

  return (
    <>
      {isDataLoading && !settlementsList.length ? (
        <Box position="absolute" top="50%" left="50%">
          <Loader colorType="warning" />
        </Box>
      ) : (
        <>
          <TableActions
            hasParams={hasParams}
            createLink={canCreateSettlement ? `/payments/create-settlement${params.caseId ? `/?caseId=${params.caseId}` : '/'
              }` : undefined}
            exportList={exportList}
            match={match}
            showFilters={showFilters}
            setShowFilters={setShowFilters}
            setMatch={setMatch}
          />

          {showFilters && (
            <TableFilters
              filters={filters}
              setFilters={setFilters}
              setMatch={setMatch}
              client={!params.caseId}
              // defendant={!params.caseId}
              timeFilter={'Entry Date'}
              statusFilter={SETTLEMENTS_STATUS_FILTER_OPTIONS}
              onboardingStatus={ONBOARDING_STATUS_FILTER_OPTIONS}
              settlementTypeFilter={SETTLEMENT_TYPE_FILTER_OPTIONS}
              approvalStatusFilter={APPROVAL_STATUS_FILTER_OPTIONS}
              match={match}
            // isPlaintiff
            />
          )}

          {!isDataLoading && settlementsList.length === 0 ? (
            <NoDataText />
          ) : (
            <TableContainer
              headerLabels={
                !params.caseId ? getSettlementsListTableLabels(localStorage.getItem('showUserOnboardingDetails') === 'true', localStorage.getItem(
                  'hasSettlementApprovalFlow'
                ) === 'true') : getCasesSettlementsListTableLabels(localStorage.getItem('showUserOnboardingDetails') === 'true', localStorage.getItem(
                  'hasSettlementApprovalFlow'
                ) === 'true')
              }
              sortableFields={sortableFields}
              activeSortField={activeSortField}
              setActiveSortField={setActiveSortField}
              handleSortTableData={handleSortTableData}
              fullWidth
            >
              <SettlementsTableDataRow tableData={settlementsList} caseId={params.caseId} />
            </TableContainer>
          )}
        </>
      )}
      <Outlet />
    </>
  );
};

export default SettlementsView;